/**
 * Validation stricte ("fermeture") d'une spec de Blueprint déjà conforme au
 * schéma Zod — même discipline anti-hallucination que le reste du repo
 * (cf. `validateDag` pour le DAG), mais sur le périmètre du Blueprint
 * uniquement : chaque fonction référencée doit appartenir au catalogue
 * candidat, chaque nom référencé (placeholder `{x}` ou argument d'expression)
 * doit être un paramètre ou un résultat déjà connu À CE POINT du solve path
 * (dépendance strictement en avant, pas de cycle possible par construction).
 *
 * Pur — aucun accès réseau/DB. Utilisé à la fois par l'edge function
 * `blueprint-build` (après l'appel LLM) et par les tests.
 */

import { evalExpr, toEvalScope } from '../shared/expr';
import type { BlueprintSpec } from './schemas';

export class BlueprintValidationError extends Error {}

// Cf. le commentaire jumeau dans generate-instance.ts (PLACEHOLDER_REGEX) :
// 1er caractère forcément minuscule pour ne jamais confondre un placeholder
// Blueprint avec du LaTeX en accolades (`\mathbb{R}`, `\frac{a}{b}`…).
const PLACEHOLDER_REGEX = /\{([a-z][a-zA-Z0-9_]*)\}/g;

function assertExpressionReferencesKnownNames(
  expr: string,
  knownNames: ReadonlySet<string>,
  context: string,
): void {
  const dummyScope: Record<string, number> = {};
  for (const name of knownNames) dummyScope[name] = 1;
  try {
    // evalExpr normalise ses identifiants en minuscules (cf. expr.ts) — les
    // noms Blueprint autorisent le camelCase ("prixUnitaire"), d'où toEvalScope.
    evalExpr(expr, toEvalScope(dummyScope));
  } catch (err) {
    throw new BlueprintValidationError(
      `${context} : expression invalide ou référence un nom inconnu "${expr}" (${(err as Error).message})`,
    );
  }
}

function assertPlaceholdersKnown(
  text: string,
  knownNames: ReadonlySet<string>,
  context: string,
): void {
  for (const match of text.matchAll(PLACEHOLDER_REGEX)) {
    const name = match[1]!;
    if (!knownNames.has(name)) {
      throw new BlueprintValidationError(
        `${context} : placeholder inconnu {${name}} dans "${text}"`,
      );
    }
  }
}

export interface ValidateBlueprintSpecOptions {
  /** Ids de fonctions candidates (catalogue @jeraisonne/db/blueprint math-functions, ou son miroir Deno). */
  candidateFunctionIds: readonly string[];
}

/** Lève une `BlueprintValidationError` au premier problème rencontré. Ne renvoie rien si tout est valide. */
export function validateBlueprintSpec(
  spec: BlueprintSpec,
  opts: ValidateBlueprintSpecOptions,
): void {
  const candidateFunctionIds = new Set(opts.candidateFunctionIds);

  const paramNames = spec.parameters.map((p) => p.name);
  if (new Set(paramNames).size !== paramNames.length) {
    throw new BlueprintValidationError('Noms de paramètres dupliqués');
  }

  const knownNames = new Set(paramNames);
  const templateAllowedNames = new Set(paramNames); // l'énoncé ne référence QUE des paramètres, jamais un résultat

  // Solve path : dépendance strictement en avant — chaque étape ne peut
  // référencer que des noms déjà connus (paramètres + résultats des étapes
  // précédentes) ET son propre résultat, s'il y en a un (le calcul d'une
  // étape s'exécute avant le rendu de son propre texte, cf. le même ordre
  // dans generate-instance.ts : `scope[resultName] = value` puis
  // `renderTemplate(step.text_md, scope)` — donc "text_md" peut conclure sur
  // "= {monResultat}" DANS LA MÊME étape, pas seulement dans une étape
  // ultérieure). Les "args" de la computation, eux, ne peuvent PAS référencer
  // le résultat qu'elle est en train de calculer (vérifiés avant l'ajout).
  for (const step of spec.solvePath) {
    if (step.computation) {
      const { functionId, resultName, args } = step.computation;
      if (!candidateFunctionIds.has(functionId)) {
        throw new BlueprintValidationError(
          `Solve path "${step.id}" : fonction inconnue ou non candidate "${functionId}"`,
        );
      }
      if (knownNames.has(resultName)) {
        throw new BlueprintValidationError(
          `Solve path "${step.id}" : nom de résultat déjà utilisé "${resultName}"`,
        );
      }
      for (const argExpr of args) {
        assertExpressionReferencesKnownNames(
          argExpr,
          knownNames,
          `Solve path "${step.id}" (argument)`,
        );
      }
      knownNames.add(resultName);
    }
    assertPlaceholdersKnown(step.text_md, knownNames, `Solve path "${step.id}"`);
  }

  for (const constraint of spec.constraints) {
    if (constraint.type === 'compare') {
      assertExpressionReferencesKnownNames(
        constraint.left,
        knownNames,
        'Contrainte compare (left)',
      );
      assertExpressionReferencesKnownNames(
        constraint.right,
        knownNames,
        'Contrainte compare (right)',
      );
    } else if (constraint.type === 'range') {
      if (!knownNames.has(constraint.variable)) {
        throw new BlueprintValidationError(
          `Contrainte range sur variable inconnue "${constraint.variable}"`,
        );
      }
    } else if (constraint.type === 'integer') {
      assertExpressionReferencesKnownNames(constraint.expression, knownNames, 'Contrainte integer');
    }
  }

  for (const template of spec.templates) {
    assertPlaceholdersKnown(template.text_md, templateAllowedNames, `Template "${template.id}"`);
  }

  // Contrairement aux templates d'énoncé (texte, où révéler un résultat serait
  // littéralement écrire la réponse), le visuel peut référencer un résultat du
  // solve path : un graphique/tableau DOIT rester fidèle à la vraie donnée pour
  // que l'exercice reste cohérent ("détermine f(x) à partir de son graphique"
  // suppose que le graphique EST le vrai f(x), coefficients calculés compris).
  // Lire une valeur sur un graphique dessiné n'est pas équivalent à la lire en
  // toutes lettres dans l'énoncé — d'où `knownNames` (params + résultats) ici,
  // et non `templateAllowedNames` (params seuls) comme pour les templates texte.
  if (spec.visual) {
    if (spec.visual.kind === 'function_graph') {
      if (knownNames.has('x')) {
        throw new BlueprintValidationError(
          'Le nom "x" (paramètre ou résultat) est réservé à la variable du graphique (visual.kind="function_graph")',
        );
      }
      for (const fn of spec.visual.functions) {
        assertPlaceholdersKnown(fn.expression, knownNames, `Visuel (fonction "${fn.id}")`);
      }
      for (const point of spec.visual.points ?? []) {
        if (typeof point.x === 'string') {
          assertExpressionReferencesKnownNames(
            point.x,
            knownNames,
            `Visuel (point "${point.id}", x)`,
          );
        }
        if (typeof point.y === 'string') {
          assertExpressionReferencesKnownNames(
            point.y,
            knownNames,
            `Visuel (point "${point.id}", y)`,
          );
        }
      }
      if (spec.visual.viewport) {
        for (const [key, value] of Object.entries(spec.visual.viewport)) {
          if (typeof value === 'string') {
            assertExpressionReferencesKnownNames(value, knownNames, `Visuel (viewport.${key})`);
          }
        }
      }
    } else {
      for (const header of spec.visual.headers ?? []) {
        assertPlaceholdersKnown(header, knownNames, 'Visuel (en-tête de tableau)');
      }
      for (const row of spec.visual.rows) {
        for (const cell of row) {
          assertPlaceholdersKnown(cell, knownNames, 'Visuel (cellule de tableau)');
        }
      }
    }
  }

  const questionIds = spec.questions.map((q) => q.id);
  if (new Set(questionIds).size !== questionIds.length) {
    throw new BlueprintValidationError('Identifiants de question dupliqués');
  }

  const solveStepIds = spec.solvePath.map((s) => s.id);
  if (new Set(solveStepIds).size !== solveStepIds.length) {
    throw new BlueprintValidationError("Identifiants d'étape de solve path dupliqués");
  }
  const knownStepIds = new Set(solveStepIds);

  for (const question of spec.questions) {
    if (!knownNames.has(question.resultName)) {
      throw new BlueprintValidationError(
        `Question "${question.id}" référence un résultat inconnu "${question.resultName}"`,
      );
    }
    assertPlaceholdersKnown(question.prompt_md, knownNames, `Question "${question.id}"`);
    for (const stepId of question.solveStepIds ?? []) {
      if (!knownStepIds.has(stepId)) {
        throw new BlueprintValidationError(
          `Question "${question.id}" : étape de solve path inconnue "${stepId}"`,
        );
      }
    }
    for (const rule of question.distractorRules ?? []) {
      if (
        (rule.kind === 'swapArgs' || rule.kind === 'formulaRef') &&
        !candidateFunctionIds.has(rule.functionId)
      ) {
        throw new BlueprintValidationError(
          `Question "${question.id}" : règle de distracteur référence une fonction inconnue "${rule.functionId}"`,
        );
      }
    }
  }
}
